import React, { useState, useEffect } from 'react';
import SpecialityDropdown from '../../Dropdown/SpecialityDropdown';
import LocationDropdown from '../../Dropdown/LocationName';
import AreaDropdown from '../../Dropdown/AreaDropdown';

const EmployeeAddModal = ({
  show,
  onClose,
  formData,
  onChange,
  onSave,
  isEditMode,
  isLoading,
}) => {
  const [validatedFields, setValidatedFields] = useState({
    emp_id: false,
    speciality: false,
    first_name: false,
    last_name: false,
    email: false,
    phone_number: false,
    location_id: false,
    area_id: false,
  });
  const [touchedFields, setTouchedFields] = useState({
    emp_id: false,
    speciality: false,
    first_name: false,
    last_name: false,
    email: false,
    phone_number: false,
    location_id: false,
    area_id: false,
  });

  // Check form validity whenever formData or validation state changes
  useEffect(() => {
    const isFormValid = Object.keys(validatedFields).every(field => {
      // Skip validation for emp_id in edit mode
      if (field === 'emp_id' && isEditMode) return true;
      // Only validate fields that have been touched or validated
      return !validatedFields[field] || !!formData[field];
    });
  }, [formData, validatedFields, isEditMode]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mark all fields as validated when submitting
    const allFieldsValidated = Object.keys(validatedFields).reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {});
    setValidatedFields(allFieldsValidated);
    setTouchedFields(allFieldsValidated);

    // Check if all required fields are filled
    const isFormValid = Object.keys(allFieldsValidated).every(field => {
      if (field === 'emp_id' && isEditMode) return true;
      return !!formData[field];
    });

    if (isFormValid) {
      onSave();
    }
  };

  const handleBlur = (fieldName) => {
    if (!touchedFields[fieldName]) {
      setTouchedFields(prev => ({
        ...prev,
        [fieldName]: true
      }));
    }
    if (!validatedFields[fieldName]) {
      setValidatedFields(prev => ({
        ...prev,
        [fieldName]: true
      }));
    }
  };

  const shouldShowValidation = (fieldName) => {
    return (validatedFields[fieldName] || touchedFields[fieldName]) && !formData[fieldName];
  };

  if (!show) return null;

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
      <div className="modal-dialog modal-dialog-scrollable modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{isEditMode ? 'Edit Employee' : 'Add Employee'}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label>Employee ID</label>
                  <input
                    type="text"
                    className={`form-control ${shouldShowValidation('emp_id') ? 'is-invalid' : ''}`}
                    name="emp_id"
                    value={formData.emp_id || ''}
                    onChange={onChange}
                    onBlur={() => handleBlur('emp_id')}
                    required={!isEditMode}
                    disabled={isEditMode}
                  />
                  {shouldShowValidation('emp_id') && !isEditMode && (
                    <div className="invalid-feedback">Employee ID is required</div>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label>Speciality</label>
                  <SpecialityDropdown
                    name="speciality"
                    value={formData.speciality}
                    onChange={onChange}
                    onBlur={() => handleBlur('speciality')}
                    hasError={shouldShowValidation('speciality')}
                  />
                  {shouldShowValidation('speciality') && (
                    <div className="invalid-feedback">Speciality is required</div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label>First Name</label>
                  <input
                    type="text"
                    className={`form-control ${shouldShowValidation('first_name') ? 'is-invalid' : ''}`}
                    name="first_name"
                    value={formData.first_name || ''}
                    onChange={onChange}
                    onBlur={() => handleBlur('first_name')}
                    required
                  />
                  {shouldShowValidation('first_name') && (
                    <div className="invalid-feedback">First name is required</div>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label>Last Name</label>
                  <input
                    type="text"
                    className={`form-control ${shouldShowValidation('last_name') ? 'is-invalid' : ''}`}
                    name="last_name"
                    value={formData.last_name || ''}
                    onChange={onChange}
                    onBlur={() => handleBlur('last_name')}
                    required
                  />
                  {shouldShowValidation('last_name') && (
                    <div className="invalid-feedback">Last name is required</div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label>Email</label>
                  <input
                    type="email"
                    className={`form-control ${shouldShowValidation('email') ? 'is-invalid' : ''}`}
                    name="email"
                    value={formData.email || ''}
                    onChange={onChange}
                    onBlur={() => handleBlur('email')}
                    required
                  />
                  {shouldShowValidation('email') && (
                    <div className="invalid-feedback">Valid email is required</div>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label>Phone</label>
                  <input
                    type="tel"
                    className={`form-control ${shouldShowValidation('phone_number') ? 'is-invalid' : ''}`}
                    name="phone_number"
                    value={formData.phone_number || ''}
                    onChange={onChange}
                    onBlur={() => handleBlur('phone_number')}
                    required
                  />
                  {shouldShowValidation('phone_number') && (
                    <div className="invalid-feedback">Phone number is required</div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label>Location</label>
                  <LocationDropdown
                    value={formData.location_id}
                    onChange={onChange}
                    onBlur={() => handleBlur('location_id')}
                    hasError={shouldShowValidation('location_id')}
                  />
                  {shouldShowValidation('location_id') && (
                    <div className="invalid-feedback">Location is required</div>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label>Area</label>
                  <AreaDropdown
                    locationId={formData.location_id}
                    value={formData.area_id}
                    onChange={onChange}
                    onBlur={() => handleBlur('area_id')}
                    hasError={shouldShowValidation('area_id')}
                  />
                  {shouldShowValidation('area_id') && (
                    <div className="invalid-feedback">Area is required</div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-outline-danger" 
                onClick={onClose} 
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    {isEditMode ? ' Updating...' : ' Saving...'}
                  </>
                ) : (
                  isEditMode ? 'Update Employee' : 'Save Employee'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmployeeAddModal;